import { getValidCenterId } from 'services/rolesEndpoint';

// Shared role/access helpers.
//
// Admin capability is PERMISSION/FLAG-BASED, not role-name-based, so renaming the
// center-admin role (e.g. `center_admin` → `center_program`) never breaks the UI.
// The only role NAMES matched here are the reserved system/assignment roles
// (master, super_admin, mentor, jury, founder) — fixed platform roles that are
// not renameable, so name matching for them is intentional and safe.
const SUPER_ADMIN_ROLES = ['superadmin', 'super_admin', 'super admin', 'master'];
// Mentor & Jury (`mentor_jury`) is one role holding both panels, so it counts
// as a juror AND as a mentor everywhere these are asked.
const MENTOR_JURY_ROLES = ['mentor_jury', 'mentor & jury', 'mentor jury'];
const JURY_MEMBER_ROLES = ['jury_member', 'jury member', 'jury', 'jurymember', 'jury panel', 'jury_panel', ...MENTOR_JURY_ROLES];
const STARTUP_FOUNDER_ROLES = ['startup_founder', 'startup founder', 'founder', 'startupfounder'];
const MENTOR_ROLES = ['mentor', ...MENTOR_JURY_ROLES];

export const isSuperAdminRole = (role) => SUPER_ADMIN_ROLES.includes((role || '').toLowerCase());

/**
 * The platform master (backend `is_platform_admin`, role_name 'master'). A
 * superset of super admin: sees the whole super-admin panel PLUS master-only
 * modules (e.g. provisioning super admins). Distinct from an organization
 * super admin, who is NOT a master.
 */
export const isMasterRole = (role) => (role || '').toLowerCase() === 'master';

export const isJuryMemberRole = (role) => JURY_MEMBER_ROLES.includes((role || '').toLowerCase());

export const isStartupFounderRole = (role) => STARTUP_FOUNDER_ROLES.includes((role || '').toLowerCase());

export const isMentorRole = (role) => MENTOR_ROLES.includes((role || '').toLowerCase());

/** The single Mentor & Jury role (both panels). */
export const isMentorJuryRole = (role) => MENTOR_JURY_ROLES.includes((role || '').toLowerCase());

/**
 * Jury, Mentor, and Startup Founder accounts may accept a collaboration
 * invitation and use a record shared with them, but may never INITIATE one —
 * inviting someone as a Collaborator is an admin-facing action, not part of
 * what these specialised roles are provisioned for. Mirrors the backend's
 * `assert_can_invite_collaborator` (app/core/roles.py) — gate the "Create
 * collaboration" / "Add organization" / "Add member" affordances with this.
 */
export const cannotInviteCollaborator = (role) =>
  isJuryMemberRole(role) || isStartupFounderRole(role) || isMentorRole(role);

/**
 * A record-scoped Collaborator (backend `COLLABORATOR_ROLE_NAME = "Collaborator"`)
 * — a fixed, non-renameable system role, so name matching is safe here like the
 * other reserved roles above. Their whole access is the per-record grant, so
 * UI meant for owners/admins (e.g. the "Collaboration" tag) is redundant for them.
 */
export const isCollaboratorRole = (role) => (role || '').toLowerCase() === 'collaborator';

/** Case-insensitive membership check against a list of role-name strings. */
export const roleMatchesAny = (role, list = []) =>
  list.map((r) => String(r).toLowerCase()).includes((role || '').toLowerCase());

// --------------------------------------------------------------------------- //
// Permission/flag-based admin capabilities (rename-safe)
// --------------------------------------------------------------------------- //
// These take a "principal" — the backend's admin signals — NOT a role-name
// string, so authorization is independent of what the center-admin role is
// called. Build one in AuthContext (`principal`) and pass THAT object here:
//
//   principal = {
//     role,         // normalized role name — used ONLY to detect the reserved
//                   //   super-admin/master names, which are never renamed
//     roleIsAdmin,  // backend `role.is_admin` flag (true for super/center/custom admins)
//     scope,        // 'organization' (org-wide super admin) | 'center' (center-scoped)
//   }
//
// A principal with no admin signals resolves to "not an admin" (fail closed).

/** Any admin — organization super admin, center admin, or a custom admin role. */
export const isAdminRole = (principal = {}) =>
  isSuperAdminRole(principal.role) || Boolean(principal.roleIsAdmin);

/** A center-scoped admin (admin capability but NOT an org super admin / master). */
export const isCenterAdminRole = (principal = {}) =>
  isAdminRole(principal) && !isSuperAdminRole(principal.role);

/**
 * May add/update the operational modules (Programs, Funds, Centers, Startups,
 * Mentoring Sessions, Document Verification, Workspace, Collaborations,
 * Stakeholder assignments, etc.). The platform master is provisioning-only
 * across these — it can VIEW them but must not create/edit/archive/approve
 * their records (the backend enforces this too, e.g. `forbid_master` on the
 * centers/funds/startups/events/evaluations routers) — so it's excluded here
 * even though it can see them (`isAdminRole` stays true). Super admins and
 * center admins keep full write access.
 *
 * The master's own write-access panels (Super Admins, Roles & Users) do NOT
 * use this helper — they gate on `isMasterRole`/no gate at all instead.
 */
const canWriteOperationalModules = (principal = {}) =>
  isAdminRole(principal) && !isMasterRole(principal.role);

/** May approve/reject pending centers (super admins + center admins, not master — the backend forbids master on these routes). */
export const canModerateCenters = canWriteOperationalModules;

/**
 * May create a Workspace project (a to-do list). Projects are set up by whoever
 * runs the center — an org super admin, a center admin, or a custom admin role.
 * A team member works INSIDE the projects they are assigned to: they add and
 * manage tasks there exactly as before, they just don't start projects of their
 * own. The backend enforces the same rule in `todo_service.create_list`, so
 * hiding the button is UI courtesy, not the boundary.
 */
export const canCreateWorkspaceProjects = isAdminRole;

/**
 * Center-scope query params. Super admins have no center → `{}` (see everything);
 * center admins get `{ center_id }` so lists show only their center's data.
 */
export const centerScopeParams = () => {
  const id = getValidCenterId();
  return id ? { center_id: id } : {};
};

export default isAdminRole;
